import React from "react";
import "@testing-library/jest-dom";
import {
  act,
  fireEvent,
  render,
  screen,
  waitFor,
} from "@testing-library/react";
import { useChatStore } from "@metalmind-ab/mmagent-chat-ui";
import Chat from "./Chat";

const mockReportFrontendError = jest.fn();

jest.mock("@metalmind-ab/mmagent-chat-ui/styles.css", () => ({}), {
  virtual: true,
});

jest.mock("../services/gptLiveVoiceTransport", () => ({
  createGptLiveVoiceTransport: () => ({
    connect: jest.fn().mockResolvedValue(undefined),
    completeDelegation: jest.fn(),
    handlePlaybackStop: jest.fn(),
    end: jest.fn().mockResolvedValue(undefined),
  }),
}));

jest.mock("../../../providers/WorkspaceProvider", () => {
  const workspaceContext = {
    workspace: {
      id: "workspace-1",
      name: "QA workspace",
      features: { mode: "chat" },
      permissions: { role: "owner" },
    },
    refreshWorkspace: jest.fn(),
  };
  return { useWorkspaceContext: () => workspaceContext };
});

jest.mock("../../../providers/FeatureFlagProvider", () => ({
  useFeatureFlag: (name: string) => name === "ida_gpt_live_enabled",
}));

jest.mock("../../../hooks/useAuthHeaders", () => {
  const getHeaders = async () => ({ Authorization: "test" });
  return { useAuthHeaders: () => getHeaders };
});

jest.mock("../../../utils/useAuthFetch", () => {
  const authFetch = async () => ({
    needs_onboarding: false,
    onboarding_agent_exists: false,
    onboarding_agent_id: null,
    workspace_name: "QA workspace",
    is_admin: false,
    welcome_description: null,
    input_placeholder: null,
    icon_suggestions: [],
    has_documents: false,
  });
  return { useAuthFetch: () => authFetch };
});

jest.mock("../../../services/frontendErrorReporting", () => ({
  ...jest.requireActual("../../../services/frontendErrorReporting"),
  createFrontendErrorReporter: () => mockReportFrontendError,
}));

jest.mock("../../../hooks/useChatImageAttachments", () => {
  const attachments = { enabled: false, uploadImage: jest.fn() };
  return { useChatImageAttachments: () => attachments };
});

jest.mock("../../../services/api", () => {
  const readUser = async () => ({});
  const listUsers = async () => [];
  return {
    useQuotaStatus: () => ({ periodicExceeded: false }),
    useReadUser: () => readUser,
    useWorkspaceUserList: () => listUsers,
  };
});

jest.mock("../services/api", () => {
  const asyncEmpty = async () => [];
  const download = async () => new Blob();
  return {
    useDownloadDocument: () => download,
    useFollowUpSuggestions: () => asyncEmpty,
    useStarterSuggestions: () => asyncEmpty,
  };
});

jest.mock("../services/collaboration", () => {
  const emptyPromise = async () => undefined;
  const listSummaries = async () => ({
    items: [
      {
        id: 8840,
        kind: "chat",
        agentStatus: "idle",
        createdAt: "2026-10-23T12:00:00Z",
        updatedAt: "2026-10-23T12:00:00Z",
      },
    ],
    total: 1,
  });
  const emptyList = async () => [];
  const createConversation = async () => 8840;
  return {
    useAddConversationCollaborators: () => emptyPromise,
    useChatConversationSummaries: () => listSummaries,
    useConversationCollaborators: () => emptyList,
    useDeleteChatConversation: () => emptyPromise,
    useRemoveConversationCollaborator: () => emptyPromise,
    useRenameChatConversation: () => emptyPromise,
    useUpdateAgentConversation: () => emptyPromise,
    useAssignAgentAvatar: () => emptyPromise,
    useAgentAvatarReservations: () => emptyList,
    useReleaseAgentAvatar: () => emptyPromise,
    useCreateEmptyChatConversation: () => createConversation,
  };
});

jest.mock("../hooks/useSendMessageWithFiles", () => {
  const sendWithFiles = async () => undefined;
  const cancelLocalStream = jest.fn();
  const upload = { sendWithFiles, isUploading: false, cancelLocalStream };
  return { useSendMessageWithFiles: () => upload };
});

jest.mock("../hooks/useBackgroundJobProgressResilience", () => ({
  isStreamableJob: () => false,
  useBackgroundJobProgressResilience: () => undefined,
}));

jest.mock("../hooks/useAgentStatusPolling", () => ({
  useAgentStatusPolling: () => ({ reconcileNow: async () => undefined }),
}));

jest.mock("../hooks/useSilentStreamRecovery", () => {
  const actual = jest.requireActual("../hooks/useSilentStreamRecovery");
  return {
    ...actual,
    useSilentStreamRecovery: ({
      reconcile,
    }: {
      reconcile: () => Promise<unknown>;
    }) => ({
      state: "error",
      recordActivity: jest.fn(),
      retry: reconcile,
    }),
  };
});

jest.mock("../hooks/useAgentStatusStream", () => ({
  useAgentStatusStream: () => ({ pushActive: false }),
}));

jest.mock("../hooks/useHeartbeatCompletionSignal", () => ({
  useHeartbeatCompletionSignal: () => undefined,
}));

jest.mock("../hooks/useJobCompletionRefresh", () => ({
  useJobCompletionRefresh: () => undefined,
}));

jest.mock("../hooks/useOnboardingExitRefresh", () => ({
  useOnboardingExitRefresh: () => undefined,
}));

jest.mock("../hooks/useWorkspaceOnboardingAutoTrigger", () => ({
  useWorkspaceOnboardingAutoTrigger: () => undefined,
}));

jest.mock("../hooks/useConversationQueue", () => ({
  useConversationQueue: () => ({
    items: [],
    error: null,
    handleFrame: jest.fn(),
    enqueue: jest.fn(),
    cancelItem: jest.fn(),
    cancelAll: jest.fn(),
  }),
}));

jest.mock("../hooks/useWorkspaceAgentSelection", () => ({
  useWorkspaceAgentSelection: () => undefined,
}));

jest.mock("../hooks/useFollowUpSuggestionChips", () => ({
  useFollowUpSuggestionChips: () => [],
}));

jest.mock("../hooks/useStarterSuggestionChips", () => ({
  useStarterSuggestionChips: () => [],
}));

jest.mock("../../../components/Tutorial", () => ({
  useTutorialTarget: () => jest.fn(),
  useTutorialHint: () => undefined,
}));

jest.mock("../components/FullPageChatLayout", () => ({
  FullPageChatLayout: (props: {
    messages?: Array<{
      id: string;
      hubMessageId?: number;
      role: "user" | "assistant";
      content: string;
    }>;
    onSendMessage: (content: string) => void | Promise<void>;
    onDeleteMessagesFrom?: (message: any) => Promise<void>;
    onTextSendStarted?: (draft: string, restoreDraft: () => void) => void;
    gptLiveVoiceControl?: React.ReactNode;
  }) => {
    const mockReact = jest.requireActual("react");
    const [draft, setDraft] = mockReact.useState("");
    const [sendComplete, setSendComplete] = mockReact.useState(false);
    return (
      <>
        {props.gptLiveVoiceControl}
        {props.onDeleteMessagesFrom && props.messages?.[0] && (
          <button
            onClick={() =>
              void props.onDeleteMessagesFrom?.(props.messages![0])
            }
          >
            Delete first message
          </button>
        )}
        {props.messages?.map((message) => (
          <span key={message.id} data-testid="chat-message">
            {message.content}
          </span>
        ))}
        <button onClick={() => props.onSendMessage("first request")}>
          Send first request
        </button>
        <textarea
          aria-label="Message"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
        <button
          onClick={() => {
            const submittedDraft = draft.trim();
            props.onTextSendStarted?.(submittedDraft, () =>
              setDraft(submittedDraft),
            );
            setDraft("");
            setSendComplete(false);
            void Promise.resolve(props.onSendMessage(submittedDraft)).then(() =>
              setSendComplete(true),
            );
          }}
        >
          Send typed draft
        </button>
        {sendComplete && <span data-testid="typed-send-complete" />}
      </>
    );
  },
}));

jest.mock("../components/DocumentPreview", () => ({
  DocumentPreview: () => null,
}));

jest.mock("../components/HeartbeatPanel", () => ({
  HeartbeatPanel: () => null,
}));

type TestStream = {
  enqueue: (chunk: Uint8Array) => void;
  signal: AbortSignal | null;
};

function createPendingStream(signal: AbortSignal | null) {
  let resolveRead:
    ((result: ReadableStreamReadResult<Uint8Array>) => void) | undefined;
  const chunks: Uint8Array[] = [];
  const finish = (result: ReadableStreamReadResult<Uint8Array>) => {
    const resolve = resolveRead;
    resolveRead = undefined;
    resolve?.(result);
  };
  const stream: TestStream = {
    signal,
    enqueue: (chunk) => {
      if (resolveRead) finish({ done: false, value: chunk });
      else chunks.push(chunk);
    },
  };
  signal?.addEventListener(
    "abort",
    () => finish({ done: true, value: undefined }),
    { once: true },
  );
  return {
    stream,
    response: {
      ok: true,
      status: 200,
      headers: new Headers(),
      body: {
        getReader: () => ({
          read: () => {
            const chunk = chunks.shift();
            if (chunk) return Promise.resolve({ done: false, value: chunk });
            return new Promise<ReadableStreamReadResult<Uint8Array>>(
              (resolve) => {
                resolveRead = resolve;
              },
            );
          },
          cancel: () => {
            finish({ done: true, value: undefined });
            return Promise.resolve();
          },
        }),
      },
    } as Response,
  };
}

function jsonResponse(value: unknown): Response {
  return {
    ok: true,
    status: 200,
    json: async () => value,
  } as Response;
}

function streamResponse(
  text: string,
  status = 200,
  headers: Record<string, string> = {},
): Response {
  const chunk = new TextEncoder().encode(text);
  const makeBody = () => {
    let sent = false;
    return {
      getReader: () => ({
        read: async () => {
          if (sent) return { done: true, value: undefined };
          sent = true;
          return { done: false, value: chunk };
        },
        releaseLock: () => undefined,
      }),
    };
  };
  const makeResponse = () => {
    const response = {
      ok: status >= 200 && status < 300,
      status,
      headers: new Headers(headers),
      body: makeBody(),
      json: async () => {
        throw new Error("not JSON");
      },
      clone: () => makeResponse(),
    };
    return response as unknown as Response;
  };
  return makeResponse();
}

describe("Chat final-status reconciliation", () => {
  const originalFetch = global.fetch;
  const originalMediaDevices = Object.getOwnPropertyDescriptor(
    navigator,
    "mediaDevices",
  );
  const originalDocumentHidden = Object.getOwnPropertyDescriptor(
    document,
    "hidden",
  );

  beforeEach(() => {
    mockReportFrontendError.mockClear();
    useChatStore.getState().clearMessages();
    useChatStore.getState().setConversationId(null);
    useChatStore.getState().setLoading(false);
  });

  afterEach(() => {
    global.fetch = originalFetch;
    if (originalMediaDevices) {
      Object.defineProperty(navigator, "mediaDevices", originalMediaDevices);
    } else {
      Reflect.deleteProperty(navigator, "mediaDevices");
    }
    if (originalDocumentHidden) {
      Object.defineProperty(document, "hidden", originalDocumentHidden);
    } else {
      Reflect.deleteProperty(document, "hidden");
    }
    jest.restoreAllMocks();
    useChatStore.getState().clearMessages();
    useChatStore.getState().setConversationId(null);
    useChatStore.getState().setLoading(false);
    sessionStorage.clear();
  });

  function mockChatFetch(
    postResponse?: Response,
    deleteResponse: Response = jsonResponse({ deleted_count: 1 }),
    messageResponse?: Response,
    postFetchError?: Error,
  ) {
    const streams = new Map<string, TestStream[]>();
    const requests: Array<{ url: string; method: string }> = [];
    let deferredMessagesRequest:
      | {
          started: () => void;
          response: Promise<Response>;
        }
      | undefined;
    global.fetch = jest.fn(async (input, init) => {
      const url = String(input);
      const method = init?.method ?? "GET";
      requests.push({ url, method });
      if (url.includes("/messages/stream")) {
        if (method === "POST" && postFetchError) throw postFetchError;
        if (method === "POST" && postResponse) return postResponse;
        const pending = createPendingStream(
          (init?.signal as AbortSignal | null) ?? null,
        );
        const list = streams.get(method) ?? [];
        list.push(pending.stream);
        streams.set(method, list);
        return pending.response;
      }
      if (url.endsWith("/queue")) return jsonResponse({ items: [] });
      if (method === "DELETE" && url.includes("/messages/")) {
        return deleteResponse;
      }
      if (url.includes("/messages")) {
        if (messageResponse) return messageResponse;
        if (deferredMessagesRequest) {
          const pending = deferredMessagesRequest;
          deferredMessagesRequest = undefined;
          pending.started();
          return pending.response;
        }
        return jsonResponse({ items: [], total: 0 });
      }
      throw new Error(`Unexpected request: ${method} ${url}`);
    }) as typeof fetch;
    return {
      streams,
      requests,
      deferNextMessagesRequest: () => {
        let resolveResponse: (response: Response) => void = () => undefined;
        let resolveStarted: () => void = () => undefined;
        const started = new Promise<void>((resolve) => {
          resolveStarted = resolve;
        });
        const response = new Promise<Response>((resolve) => {
          resolveResponse = resolve;
        });
        deferredMessagesRequest = {
          started: resolveStarted,
          response,
        };
        return { started, resolve: resolveResponse };
      },
    };
  }

  function renderChat() {
    render(<Chat />);
    useChatStore.getState().setConversationId("8840");
  }

  function conversationHistoryRequests(
    requests: Array<{ url: string; method: string }>,
  ): string[] {
    return requests
      .filter(
        ({ url, method }) =>
          method === "GET" &&
          url.includes("/chat/conversations/8840/messages") &&
          !url.includes("/messages/stream"),
      )
      .map(({ url }) => url);
  }

  async function reconcileFinalStatus() {
    fireEvent.click(screen.getByRole("button", { name: "Retry" }));
    await new Promise((resolve) => setTimeout(resolve, 0));
  }

  it("restores saved history while hidden without starting foreground polling", async () => {
    const { requests } = mockChatFetch();
    sessionStorage.setItem("ida-conversation-workspace-1", "8840");
    Object.defineProperty(document, "hidden", {
      configurable: true,
      value: true,
    });
    const intervalSpy = jest.spyOn(window, "setInterval");

    render(<Chat />);

    await waitFor(() =>
      expect(conversationHistoryRequests(requests)).toHaveLength(1),
    );
    expect(conversationHistoryRequests(requests)[0]).toContain(
      "include_active_jobs=true",
    );
    expect(intervalSpy.mock.calls.some(([, delay]) => delay === 10_000)).toBe(
      false,
    );
  });

  it("starts the immediate foreground poll for a visible restored conversation", async () => {
    const { requests } = mockChatFetch();
    sessionStorage.setItem("ida-conversation-workspace-1", "8840");
    Object.defineProperty(document, "hidden", {
      configurable: true,
      value: false,
    });
    const intervalSpy = jest.spyOn(window, "setInterval");

    render(<Chat />);

    await waitFor(() =>
      expect(conversationHistoryRequests(requests)).toHaveLength(2),
    );
    const historyRequests = conversationHistoryRequests(requests);
    expect(
      historyRequests.some((url) => url.includes("include_active_jobs=true")),
    ).toBe(true);
    expect(
      historyRequests.some((url) => !url.includes("include_active_jobs=true")),
    ).toBe(true);
    expect(intervalSpy.mock.calls.some(([, delay]) => delay === 10_000)).toBe(
      true,
    );
  });

  it("can issue duplicate restore GETs when mount effects replay in StrictMode", async () => {
    const { requests } = mockChatFetch();
    sessionStorage.setItem("ida-conversation-workspace-1", "8840");
    Object.defineProperty(document, "hidden", {
      configurable: true,
      value: true,
    });

    render(
      <React.StrictMode>
        <Chat />
      </React.StrictMode>,
    );

    await waitFor(() =>
      expect(conversationHistoryRequests(requests)).toHaveLength(2),
    );
    expect(
      conversationHistoryRequests(requests).every((url) =>
        url.includes("include_active_jobs=true"),
      ),
    ).toBe(true);
  });

  it("starts microphone capture after Chat creates and selects an empty conversation", async () => {
    const { streams } = mockChatFetch();
    const track = { stop: jest.fn() };
    const stream = {
      getTracks: () => [track],
      getAudioTracks: () => [track],
    } as unknown as MediaStream;
    const getUserMedia = jest.fn().mockResolvedValue(stream);
    Object.defineProperty(navigator, "mediaDevices", {
      configurable: true,
      value: { getUserMedia },
    });

    render(<Chat />);
    const startButton = await screen.findByRole("button", {
      name: "Start voice conversation",
    });
    fireEvent.click(startButton);

    await waitFor(() =>
      expect(getUserMedia).toHaveBeenCalledWith({ audio: true }),
    );
    await waitFor(() =>
      expect(screen.getByTestId("gpt-live-voice-state").textContent).toBe(
        "Listening",
      ),
    );
    expect(streams.get("GET")?.length).toBeGreaterThan(0);
  });

  it("reports message-history HTTP failures with bounded request context", async () => {
    mockChatFetch(undefined, undefined, streamResponse("unavailable", 503));
    renderChat();

    await waitFor(() =>
      expect(mockReportFrontendError).toHaveBeenCalledWith(
        expect.objectContaining({
          message: "IDA conversation history request failed",
        }),
        "",
        "chat_history",
        expect.objectContaining({
          kind: "history_get",
          phase: "http_response",
          status: 503,
          requestId: expect.stringMatching(/^[0-9a-f]{32}$/),
        }),
      ),
    );
  });

  it("keeps a new empty conversation's passive GET open during real reconciliation", async () => {
    const { streams } = mockChatFetch();
    renderChat();
    await waitFor(() => expect(streams.get("GET")?.length).toBeGreaterThan(0));
    const passive =
      streams.get("GET")!.find((stream) => stream.signal) ??
      streams.get("GET")![0];

    await reconcileFinalStatus();

    expect(passive.signal?.aborted).toBe(false);
  });

  it("restores a typed draft when the POST fails before its stream starts", async () => {
    mockChatFetch(streamResponse("unavailable", 503));
    renderChat();
    fireEvent.change(await screen.findByRole("textbox", { name: "Message" }), {
      target: { value: "retry this request" },
    });

    fireEvent.click(screen.getByRole("button", { name: "Send typed draft" }));

    expect(useChatStore.getState().isLoading).toBe(true);
    await waitFor(() => expect(useChatStore.getState().isLoading).toBe(false));
    await waitFor(() =>
      expect(screen.getByRole("textbox", { name: "Message" })).toHaveValue(
        "retry this request",
      ),
    );
  });

  it.each<[string, RegExp, RegExp, boolean]>([
    [
      "Failed to execute 'fetch' on 'Window': Invalid value",
      /problem in the app, not your connection/i,
      /may be a network problem/i,
      true,
    ],
    [
      "Failed to fetch",
      /may be a network problem/i,
      /problem in the app/i,
      false,
    ],
  ])(
    "shows the correct notice and keeps the draft after fetch rejection (%s)",
    async (errorMessage, expectedNotice, otherNotice, showsGenericError) => {
      mockChatFetch(
        undefined,
        undefined,
        undefined,
        new TypeError(errorMessage),
      );
      renderChat();
      fireEvent.change(
        await screen.findByRole("textbox", { name: "Message" }),
        {
          target: { value: "retry this blocked request" },
        },
      );

      fireEvent.click(screen.getByRole("button", { name: "Send typed draft" }));

      expect(await screen.findByText(expectedNotice)).toBeInTheDocument();
      expect(screen.getByRole("textbox", { name: "Message" })).toHaveValue(
        "retry this blocked request",
      );
      expect(screen.queryByText(otherNotice)).not.toBeInTheDocument();
      expect(screen.queryByText(/something went wrong/i) !== null).toBe(
        showsGenericError,
      );
    },
  );

  it("leaves the composer empty after a typed send succeeds", async () => {
    mockChatFetch(
      streamResponse(
        'data: {"type":"done","submission_id":"submission-success"}\n\n',
        200,
        {
          "Content-Type": "text/event-stream",
          "X-Chat-Submission-ID": "submission-success",
          "X-Chat-Conversation-ID": "8840",
        },
      ),
    );
    renderChat();
    fireEvent.change(await screen.findByRole("textbox", { name: "Message" }), {
      target: { value: "successful request" },
    });

    fireEvent.click(screen.getByRole("button", { name: "Send typed draft" }));

    expect(useChatStore.getState().isLoading).toBe(true);
    await waitFor(() => expect(useChatStore.getState().isLoading).toBe(false));
    await screen.findByTestId("typed-send-complete");
    expect(screen.getByRole("textbox", { name: "Message" })).toHaveValue("");
  });

  it("does not restore a typed draft when failure follows the first stream frame", async () => {
    mockChatFetch(
      streamResponse(
        'data: {"type":"assistant_turn_started","assistant_turn_id":"turn-1","submission_id":"submission-streamed"}\n\n' +
          'data: {"type":"error","submission_id":"submission-streamed","error":"request failed"}\n\n',
        200,
        {
          "Content-Type": "text/event-stream",
          "X-Chat-Submission-ID": "submission-streamed",
          "X-Chat-Conversation-ID": "8840",
        },
      ),
    );
    renderChat();
    fireEvent.change(await screen.findByRole("textbox", { name: "Message" }), {
      target: { value: "do not restore this" },
    });

    fireEvent.click(screen.getByRole("button", { name: "Send typed draft" }));

    expect(useChatStore.getState().isLoading).toBe(true);
    await waitFor(() => expect(useChatStore.getState().isLoading).toBe(false));
    await screen.findByTestId("typed-send-complete");
    expect(screen.getByRole("textbox", { name: "Message" })).toHaveValue("");
  });

  it("keeps the row and shows an error when its Hub message ID cannot be resolved", async () => {
    const { requests } = mockChatFetch();
    renderChat();
    useChatStore.getState().setMessages([
      {
        id: "client-generated-id",
        role: "user",
        content: "message kept until server deletion",
        createdAt: new Date("2026-10-26T12:00:00Z"),
      },
    ]);
    fireEvent.click(
      await screen.findByRole("button", { name: "Delete first message" }),
    );

    expect(
      await screen.findByText(
        "Could not delete this message. Please try again.",
      ),
    ).toBeInTheDocument();
    expect(
      screen.getByText("message kept until server deletion"),
    ).toBeInTheDocument();
    expect(requests.some(({ method }) => method === "DELETE")).toBe(false);
    expect(mockReportFrontendError).toHaveBeenCalledWith(
      expect.objectContaining({
        name: "chat_delete_unresolved_id",
        message: "The persisted message ID could not be resolved.",
      }),
      "",
      "window_error",
    );

    fireEvent.click(
      screen.getByRole("button", { name: "Dismiss delete message error" }),
    );
    expect(
      screen.queryByText("Could not delete this message. Please try again."),
    ).not.toBeInTheDocument();
  });

  it.each([403, 500])(
    "keeps the row and shows an error when the DELETE request returns %i",
    async (status) => {
      const { requests } = mockChatFetch(undefined, {
        ok: false,
        status,
        headers: new Headers(),
      } as Response);
      renderChat();
      useChatStore.getState().setMessages([
        {
          id: "901",
          hubMessageId: 901,
          role: "user",
          content: "message kept after rejection",
          createdAt: new Date("2026-10-26T12:00:00Z"),
        },
      ]);
      fireEvent.click(
        await screen.findByRole("button", { name: "Delete first message" }),
      );

      expect(
        await screen.findByText(
          "Could not delete this message. Please try again.",
        ),
      ).toBeInTheDocument();
      expect(
        screen.getByText("message kept after rejection"),
      ).toBeInTheDocument();
      expect(
        requests.some(
          ({ url, method }) =>
            method === "DELETE" && url.endsWith("/messages/901"),
        ),
      ).toBe(true);
      expect(mockReportFrontendError).toHaveBeenCalledWith(
        expect.objectContaining({
          name: "chat_delete_request_failed",
          message: `The message DELETE returned HTTP ${status}.`,
        }),
        "",
        "window_error",
      );
    },
  );

  it("removes the row after the DELETE request succeeds", async () => {
    mockChatFetch();
    renderChat();
    useChatStore.getState().setMessages([
      {
        id: "902",
        hubMessageId: 902,
        role: "user",
        content: "message removed after success",
        createdAt: new Date("2026-10-26T12:00:00Z"),
      },
    ]);
    fireEvent.click(
      await screen.findByRole("button", { name: "Delete first message" }),
    );

    await waitFor(() =>
      expect(
        screen.queryByText("message removed after success"),
      ).not.toBeInTheDocument(),
    );
    expect(
      screen.queryByText("Could not delete this message. Please try again."),
    ).not.toBeInTheDocument();
  });

  it("keeps submission 2 frames after terminal reconciliation for submission 1", async () => {
    const { streams } = mockChatFetch();
    renderChat();
    await waitFor(() => expect(streams.get("GET")?.length).toBeGreaterThan(0));
    const passive = streams.get("GET")![0];

    fireEvent.click(screen.getByRole("button", { name: "Send first request" }));
    await waitFor(() => expect(streams.get("POST")?.length).toBeGreaterThan(0));
    const post = streams.get("POST")![0];
    await act(async () => {
      post.enqueue(
        new TextEncoder().encode(
          'id: first-start\ndata: {"type":"assistant_turn_started","assistant_turn_id":"turn-1","conversation_id":"8840","submission_id":"submission-1"}\n\n',
        ),
      );
      passive.enqueue(
        new TextEncoder().encode(
          'id: second-queued\ndata: {"type":"queued","conversation_id":"8840","submission_id":"submission-2","position":1}\n\n',
        ),
      );
    });
    await act(async () => {
      post.enqueue(
        new TextEncoder().encode(
          'id: first-done\ndata: {"type":"done","assistant_turn_id":"turn-1","conversation_id":"8840","submission_id":"submission-1"}\n\n',
        ),
      );
    });
    await waitFor(() => expect(useChatStore.getState().isLoading).toBe(false));

    await reconcileFinalStatus();
    expect(passive.signal?.aborted).toBe(false);

    await act(async () => {
      passive.enqueue(
        new TextEncoder().encode(
          'id: second-start\ndata: {"type":"assistant_turn_started","assistant_turn_id":"turn-2","conversation_id":"8840","submission_id":"submission-2"}\n\n' +
            'id: second-content\ndata: {"type":"content_delta","assistant_turn_id":"turn-2","conversation_id":"8840","submission_id":"submission-2","content":"Second response"}\n\n',
        ),
      );
    });
    await waitFor(() =>
      expect(useChatStore.getState().messages).toEqual(
        expect.arrayContaining([
          expect.objectContaining({
            role: "assistant",
            content: "Second response",
          }),
        ]),
      ),
    );
    expect(passive.signal?.aborted).toBe(false);
  });

  it("keeps a per-job failure that arrives while final-status reconciliation waits", async () => {
    const { deferNextMessagesRequest } = mockChatFetch();
    renderChat();
    await screen.findByRole("button", { name: "Retry" });
    await waitFor(() =>
      expect(useChatStore.getState().conversationId).toBe("8840"),
    );

    const runningMessage = {
      id: "assistant-document-turn",
      role: "assistant" as const,
      content: "Creating the document",
      createdAt: new Date("2026-10-24T10:00:00Z"),
      toolExecutions: [
        {
          id: "document-call-1",
          name: "generate_document",
          args: {},
          status: "running" as const,
          result: { background: true, job_id: "document-job-1" },
        },
      ],
    };
    useChatStore.getState().setMessages([runningMessage]);

    const pendingHistory = deferNextMessagesRequest();
    fireEvent.click(screen.getByRole("button", { name: "Retry" }));
    await pendingHistory.started;

    const failedMessage = {
      ...runningMessage,
      toolExecutions: [
        {
          ...runningMessage.toolExecutions[0],
          status: "error" as const,
          error: "Document generation failed",
        },
      ],
    };
    act(() => useChatStore.getState().setMessages([failedMessage]));
    pendingHistory.resolve(
      jsonResponse({
        items: [
          {
            ...runningMessage,
            hubMessageId: 901,
          },
        ],
        total: 1,
      }),
    );

    await waitFor(() => {
      expect(
        useChatStore.getState().messages[0].toolExecutions?.[0],
      ).toMatchObject({
        status: "error",
        error: "Document generation failed",
        result: { job_id: "document-job-1" },
      });
    });
  });
});
